//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import Text, { BodyErrorText, BodyText } from '@components/atoms/text/Text';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { HashtagButton } from '@components/molecules/smart-input/recommended-hashtags/HashtagButton';
import { HashtagDropdownTitle } from '@components/molecules/smart-input/recommended-hashtags/HashtagDropdownTitle';
import { insertHashtag } from '@components/molecules/smart-input/recommended-hashtags/hashtagUtils';
import { IRecommendedHashtag } from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { usePrevious } from '@hooks/usePrevious';
import { Mixins } from '@theme/mixins';
import { isTruthy } from '@utils/filterPredicates';

export const HashtagDropdown = () => {
  const smartInputContext = useSmartInputContext();
  const { recommendedHashtags, trackingIdPrefix } = smartInputContext;

  const isRecommendedAiHashtagsEnabled =
    recommendedHashtags?.isRecommendedAiHashtagsEnabled ?? false;
  const isRecommendedAiHashtagsEnabledForPlanTier =
    recommendedHashtags?.isRecommendedAiHashtagsEnabledForPlanTier ?? false;
  const isLoadingRecommendedAiHashtags =
    recommendedHashtags?.isLoadingRecommendedAiHashtags ?? false;

  const isRecommendedRecentHashtagsEnabled =
    recommendedHashtags?.isRecommendedRecentHashtagsEnabled ?? false;

  const [isRecentHashtagsSelected, setIsRecentHashtagsSelected] = useState(
    !!isRecommendedRecentHashtagsEnabled,
  );

  const recommendedRecentHashtags =
    recommendedHashtags?.recommendedRecentHashtags ?? [];
  const recommendedAiHashtags =
    recommendedHashtags?.recommendedAiHashtags ?? [];

  useEffect(() => {
    if (
      isRecommendedAiHashtagsEnabled &&
      isRecommendedAiHashtagsEnabledForPlanTier &&
      // We only want to fetch when the dropdown is open
      recommendedHashtags?.isDropdownOpen
    ) {
      void recommendedHashtags.getRecommendedAiHashtags?.();
    }
  }, [
    isRecommendedAiHashtagsEnabled,
    isRecommendedAiHashtagsEnabledForPlanTier,
    recommendedHashtags?.isDropdownOpen,
  ]);

  const hasRecommendedRecentHashtagsToShow = recommendedRecentHashtags.some(
    (tag: IRecommendedHashtag) => tag.shouldShow,
  );
  const prevHasRecommendedRecentHashtags = usePrevious(
    hasRecommendedRecentHashtagsToShow,
  );

  const hasRecommendedAiHashtagsToShow = recommendedAiHashtags.some(
    (tag: IRecommendedHashtag) => tag.shouldShow,
  );
  const prevHasRecommendedAiHashtags = usePrevious(
    hasRecommendedAiHashtagsToShow,
  );

  const closeDropdown = () => {
    recommendedHashtags?.setIsDropdownOpen(false, true);
  };

  const handleHashtagClick = (tag: string) => {
    insertHashtag(tag, smartInputContext);
  };

  // This effect allows this dropdown to close when there are no more recommendedRecentHashtags or recommendedAiHashtags to be added,
  // but open with the EmptyText when reopened
  useEffect(() => {
    if (isRecentHashtagsSelected) {
      if (
        prevHasRecommendedRecentHashtags &&
        !hasRecommendedRecentHashtagsToShow
      ) {
        closeDropdown();
      }
    } else {
      if (
        !isLoadingRecommendedAiHashtags &&
        prevHasRecommendedAiHashtags &&
        !hasRecommendedAiHashtagsToShow
      ) {
        closeDropdown();
      }
    }
  });

  const emptyState = (() => {
    if (!isRecentHashtagsSelected && isLoadingRecommendedAiHashtags) {
      return (
        <HashtagsContainer>
          <CenteredSpinner />
        </HashtagsContainer>
      );
    }

    if (
      !isRecentHashtagsSelected &&
      isTruthy(recommendedHashtags?.errorMessage)
    ) {
      return (
        <StyledErrorText>{recommendedHashtags.errorMessage}</StyledErrorText>
      );
    }

    return (
      <EmptyText mixin={Mixins.Text.UiCopySecondary}>
        You've added all of your{' '}
        {isRecentHashtagsSelected ? 'most used' : 'AI recommended'} hashtags.
      </EmptyText>
    );
  })();

  return (
    <DropdownWrapper tabIndex={-1}>
      <CloseButton
        iconName='close'
        onClick={closeDropdown}
        trackingId={getSmartInputTrackingId(
          trackingIdPrefix,
          SmartInputTrackingId.CloseRecommenedHashtagsDropdownButton,
        )}
      />
      <TitleWrapper>
        <HashtagDropdownTitle
          disabledText={recommendedHashtags?.disabledText}
          disabledTierTooltipContent={
            recommendedHashtags?.disabledTierTooltipContent
          }
          isRecentHashtagsSelected={isRecentHashtagsSelected}
          isRecommendedAiHashtagsEnabled={isRecommendedAiHashtagsEnabled}
          isRecommendedAiHashtagsEnabledForPlanTier={
            isRecommendedAiHashtagsEnabledForPlanTier
          }
          setIsRecentHashtagsSelected={setIsRecentHashtagsSelected}
          shouldDisplayToggle={
            isRecommendedAiHashtagsEnabled && isRecommendedRecentHashtagsEnabled
          }
        />
      </TitleWrapper>
      <HashtagsWrapper>
        {!isRecentHashtagsSelected && (
          <BodyText>AI recommended hashtags based on the platform.</BodyText>
        )}
        {(
          isRecentHashtagsSelected
            ? hasRecommendedRecentHashtagsToShow
            : !isLoadingRecommendedAiHashtags && hasRecommendedAiHashtagsToShow
        ) ? (
          <HashtagsContainer role='list'>
            {(isRecentHashtagsSelected
              ? recommendedRecentHashtags
              : recommendedAiHashtags
            ).map((tag) => (
              <HashtagButton
                key={tag.value}
                onClick={handleHashtagClick}
                recommendation={tag}
              />
            ))}
          </HashtagsContainer>
        ) : (
          emptyState
        )}
      </HashtagsWrapper>
    </DropdownWrapper>
  );
};

const DropdownWrapper = styled.div(
  ({ theme }) => `
  ${Mixins.Sheet.High}
  flex-direction: column;
  position: absolute;
  margin: 2px 0 0;
  padding: ${theme.spacings[3]} ${theme.spacings[3]} 0;
  width: 100%;
  
  /**
   * From design: This height accommodates 4.5 rows of labels. Given the average character count of a
   * hashtag, we expect ~2 labels to fit in each row, so ~8 full hashtags will be fully visible by
   * default. Brands are typically adding 3-4 hashtags per caption. This means that Bucky will easily
   * be able to visualize his top 3-4 captions that he's frequently re-using in captions 
   */
  max-height: 204px;
  min-height: ${theme.spacings[7]};
  overflow: hidden;
  z-index: ${theme.zindex.fixed};

  &:focus {
    outline: none;
  }
  `,
);

const CloseButton = styled(ActionButton)(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[2]};
  right: ${theme.spacings[2]};
`,
);

const TitleWrapper = styled.div(
  ({ theme }) => `
  align-self: flex-start;
  display: flex;
  align-items: center;
  margin: 0 0 ${theme.spacings[2]};
`,
);

const HashtagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  overflow: auto;
`;

const EmptyText = styled(Text)(
  ({ theme }) => `
  width: 100%;
  margin: 0 auto ${theme.spacings[2]};
  padding: ${theme.spacings[2]};
  text-align: center;
`,
);

const StyledErrorText = styled(BodyErrorText)(
  ({ theme }) => `
  width: 100%;
  margin: 0 auto ${theme.spacings[2]};
  padding: ${theme.spacings[2]};
  text-align: center;
`,
);

const HashtagsWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
  overflow-y: auto;
`,
);
